/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module CollectionsModule
 */

import { Collection } from 'ajs/modules/data-model/factories/collection.factory';
import { VirtualServiceItem } from 'items/virtual-service.item.factory';
import { VirtualServiceType } from 'generated-types';

/**
 * Type of params passed to drop method in item factory.
 */
export interface IVSDropParams {
    withChildren: boolean;
}

const allDataSources = {
    inventory: {
        source: 'InventoryCollDataSource',
        transformer: 'VSInventoryDataTransformer',
        transport: 'ListDataTransport',
        fields: [
            'config',
            'health',
            'runtime',
            'alert',
            'faults',
        ],
    },
    'collection-metrics': {
        source: 'CollMetricsCollDataSource',
        transformer: 'CollMetricsDataTransformer',
        transport: 'CollMetricsDataTransport',
        fields: [
            'l4_client.avg_bandwidth',
            'l4_client.max_num_active_se',
            'l4_client.max_open_conns',
            'l4_client.avg_total_rtt',
            'l4_server.avg_total_rtt',
            'l4_client.avg_complete_conns',
            'l4_client.avg_rx_pkts',
            'l4_client.avg_tx_pkts',
            'l4_client.avg_policy_drops',
            'l4_client.avg_dos_attacks',
            'l4_client.avg_errored_connections',
            'l7_client.avg_complete_responses',
            'l7_client.avg_error_responses',
            'l7_client.pct_response_errors',
        ],
        dependsOn: 'config',
    },
};

export type TVirtualServiceCollection = typeof VirtualServiceCollection;

/**
 * @description VirtualService collection
 * @author Guru Prasad H K
 */
export class VirtualServiceCollection extends Collection {
    public static ngDependencies = [VirtualServiceItem];

    constructor(args = {} as any) {
        const { params = {}, ...restArgs } = args;
        const extendedArgs = {
            objectName: 'virtualservice-inventory',
            windowElement: 'lazy-load',
            isStatic: false,
            sortBy: 'name',
            ...restArgs,
            params: {
                includeName_: true,
                ...params,
            },
        };

        const { objectName } = extendedArgs;

        // if the object name is pool-inventory, load by the inventory API
        // otherwise use default data source settings to load by the list (pool) API
        if (objectName === 'virtualservice-inventory') {
            extendedArgs.allDataSources = allDataSources;
            extendedArgs.defaultDataSources = 'inventory';
        }

        super(extendedArgs);

        this.itemClass_ = this.getNgDependency(VirtualServiceItem);
        this.serverDefaultsOverride_ = {
            services: [this.itemClass_.getDefaultServiceConfig({ port: 80 })],
            connections_rate_limit: undefined,
            requests_rate_limit: undefined,
            se_group_ref: undefined,
            saml_sp_config: undefined,
            jwt_config: undefined,
            ldap_vs_config: undefined,
            oauth_vs_config: undefined,
            analytics_policy: {
                client_log_filters: [],
                full_client_logs: {},
                metrics_realtime_update: {},
            },
        };
    }

    /**
     * Virtual Services cannot be dropped by any order. We need to make sure that,
     * Child VSes are dropped first.
     * @param force - True if deleting VS along with children.
     */
    public dropByPriority(items: VirtualServiceItem[], force: boolean): Promise<void> {
        const childVirtualServices: string[] = [];
        const otherVirtualServices: string[] = [];

        items.forEach((item: VirtualServiceItem) => {
            const config = item.getConfig();

            if (config.type === VirtualServiceType.VS_TYPE_VH_CHILD) {
                childVirtualServices.push(item.id);
            } else {
                otherVirtualServices.push(item.id);
            }
        });

        let dropParams: IVSDropParams;

        if (force) {
            dropParams = {
                withChildren: true,
            };
        }

        const promise = childVirtualServices.length ?
            this.dropItems(childVirtualServices, false, false, dropParams) : Promise.resolve(true);

        return promise
            .then(() => {
                return otherVirtualServices.length ?
                    this.dropItems(otherVirtualServices, false, false, dropParams) :
                    Promise.resolve(true);
            });
    }
}
